/**
 * Where a push notification lands when the vendor taps it.
 *
 * Extracted out of `MessagingService` so the rule can be tested: the same
 * three lines also live in `src/firebase-messaging-sw.js`, which is a plain
 * service-worker script and cannot import from the app bundle. **The two
 * copies must be changed together** — the worker decides the destination when
 * the tab is closed, the service decides it when the tab is open, and a vendor
 * who taps the same notification twice must not land in two different places.
 *
 * Payloads (`feat/martech-coupons-discounts`):
 * - booking alerts: `{ type: 'booking', bookingId, notificationId, status }`
 * - coupon alerts:  `{ type: 'coupon', couponId, status, notificationId }`
 *   (`coupon-notify.helper.ts:91-100`) — these carry **no `bookingId`**.
 *
 * An id is checked before it is interpolated. A coupon alert reaching the old
 * booking rule, or a malformed payload, would otherwise navigate to
 * `/bookings/undefined` — a page that renders an error instead of telling the
 * vendor what happened.
 */

/** The `data` block of an FCM message. Every FCM value arrives as a string. */
export interface PushDeepLinkData {
  type?: string;
  bookingId?: string;
  couponId?: string;
  notificationId?: string;
  status?: string;
  url?: string;
}

/**
 * `'undefined'` and `'null'` are real strings on the wire: a backend that
 * interpolates a missing id sends the WORD, which is truthy and would sail
 * straight into the URL.
 */
const routableId = (value: unknown): string => {
  const id = typeof value === 'string' ? value.trim() : '';
  return id && id !== 'undefined' && id !== 'null' ? id : '';
};

/** The in-app route for a payload, or `/` when there is nothing to open. */
export function deepLinkFor(
  data: PushDeepLinkData | null | undefined,
): string {
  if (!data) return '/';
  // An explicit url from the server wins — it is the only way to deep-link a
  // destination this build does not know about yet.
  if (typeof data.url === 'string' && data.url) return data.url;
  if (data.type === 'booking' && routableId(data.bookingId)) {
    return `/bookings/${routableId(data.bookingId)}`;
  }
  if (data.type === 'coupon' && routableId(data.couponId)) {
    return `/coupons/${routableId(data.couponId)}`;
  }
  return '/';
}
